type GAEventParams = Record<string, string | number | boolean | undefined | null>;

const trackedPageEvents = new Set<string>();
const trackedConversionEvents = new Set<string>();
const trackedPredConvEvents = new Set<string>();
let interstitialPredConvTrackingInitialized = false;

const PRED_CONV_SEND_TO = 'AW-17723879355/ajPbCN7dkescELvfs4NC';
const PRED_CONV_TRACKED_KEY = 'pred_conv_tracked_keys';

const getDeviceType = () => {
  if (typeof window === 'undefined') {
    return 'unknown';
  }

  return window.matchMedia('(max-width: 768px)').matches ? 'mobile' : 'desktop';
};

const ensureGtag = () => {
  const win = window as any;
  win.dataLayer = win.dataLayer || [];
  win.gtag = win.gtag || function gtag() {
    win.dataLayer.push(arguments);
  };

  return win.gtag;
};

const hasPredConvTracked = (eventKey: string) => {
  if (trackedPredConvEvents.has(eventKey)) {
    return true;
  }

  try {
    const storedKeys = JSON.parse(window.sessionStorage.getItem(PRED_CONV_TRACKED_KEY) || '[]');
    return Array.isArray(storedKeys) && storedKeys.includes(eventKey);
  } catch {
    return false;
  }
};

const markPredConvTracked = (eventKey: string) => {
  trackedPredConvEvents.add(eventKey);

  try {
    const storedKeys = JSON.parse(window.sessionStorage.getItem(PRED_CONV_TRACKED_KEY) || '[]');
    const nextKeys = Array.isArray(storedKeys)
      ? [...storedKeys.filter((key) => typeof key === 'string'), eventKey]
      : [eventKey];
    window.sessionStorage.setItem(PRED_CONV_TRACKED_KEY, JSON.stringify(Array.from(new Set(nextKeys)).slice(-100)));
  } catch {
    // In-memory dedupe still works when sessionStorage is unavailable.
  }
};

export const trackGAEvent = (eventName: string, params: GAEventParams = {}) => {
  if (typeof window === 'undefined') {
    return;
  }

  const eventKey = `${eventName}:${window.location.pathname}:${params.app_id ?? ''}`;
  if (trackedPageEvents.has(eventKey)) {
    return;
  }
  trackedPageEvents.add(eventKey);

  const gtag = ensureGtag();

  gtag('event', eventName, {
    page_location: window.location.href,
    page_path: window.location.pathname,
    ...params,
  });
};

export const trackGoogleAdsConversion = (
  conversionId: string,
  conversionLabel: string,
  params: GAEventParams = {}
) => {
  if (typeof window === 'undefined') {
    return;
  }

  const sendTo = `AW-${conversionId}/${conversionLabel}`;
  const eventKey = `${sendTo}:${window.location.pathname}:${params.app_id ?? ''}`;
  if (trackedConversionEvents.has(eventKey)) {
    return;
  }
  trackedConversionEvents.add(eventKey);

  const gtag = ensureGtag();

  gtag('event', 'conversion', {
    send_to: sendTo,
    page_location: window.location.href,
    page_path: window.location.pathname,
    ...params,
  });
};

export const trackPredConv = (params: GAEventParams = {}) => {
  if (typeof window === 'undefined') {
    return;
  }

  if (params.signal_type !== 'iframe_pred' && params.signal_type !== 'pointer_then_iframe_pred') {
    return;
  }

  const eventParams: GAEventParams = {
    page_location: window.location.href,
    page_path: window.location.pathname,
    device_type: getDeviceType(),
    event_source: 'adsense_attention_prediction',
    ...params,
  };

  const eventKey = [
    'pred_conv',
    window.location.pathname,
    eventParams.ad_position ?? '',
  ].join(':');

  if (hasPredConvTracked(eventKey)) {
    return;
  }
  markPredConvTracked(eventKey);

  const gtag = ensureGtag();

  gtag('event', 'pred_conv', eventParams);
  gtag('event', 'conversion', {
    send_to: PRED_CONV_SEND_TO,
    ...eventParams,
  });
};

export const initInterstitialPredConvTracking = () => {
  if (typeof window === 'undefined' || interstitialPredConvTrackingInitialized) {
    return;
  }

  interstitialPredConvTrackingInitialized = true;
  let interstitialIframe: HTMLIFrameElement | null = null;
  let interstitialPointerActive = false;
  let interstitialPointerTimer: number | undefined;
  const interstitialFocusTimers: number[] = [];

  const findInterstitialIframe = () => {
    interstitialIframe = document.querySelector('ins[data-vignette-loaded="true"] iframe');
    return interstitialIframe;
  };

  const armInterstitialTracking = () => {
    if (window.location.hash !== '#google_vignette') {
      return;
    }

    window.setTimeout(() => {
      const iframe = findInterstitialIframe();
      if (iframe) {
        iframe.blur();
      }
    }, 100);
  };

  const sendIfInterstitialFocused = () => {
    const iframe = interstitialIframe || findInterstitialIframe();
    if (!iframe || document.activeElement !== iframe) {
      return;
    }

    trackPredConv({
      ad_type: 'interstitial',
      signal_type: interstitialPointerActive ? 'pointer_then_iframe_pred' : 'iframe_pred',
      ad_position: 'adsense_auto_interstitial',
    });
    interstitialPointerActive = false;
  };

  const scheduleInterstitialFocusChecks = () => {
    [100, 300, 600].forEach((delay) => {
      interstitialFocusTimers.push(window.setTimeout(sendIfInterstitialFocused, delay));
    });
  };

  const handleInterstitialPointer = () => {
    if (window.location.hash !== '#google_vignette') {
      return;
    }

    interstitialPointerActive = true;
    if (interstitialPointerTimer) {
      window.clearTimeout(interstitialPointerTimer);
    }
    interstitialPointerTimer = window.setTimeout(() => {
      interstitialPointerActive = false;
    }, 800);
    scheduleInterstitialFocusChecks();
  };

  window.addEventListener('hashchange', armInterstitialTracking);
  window.addEventListener('pointerdown', handleInterstitialPointer, { capture: true, passive: true });
  window.addEventListener('touchstart', handleInterstitialPointer, { capture: true, passive: true });
  window.addEventListener('mousedown', handleInterstitialPointer, { capture: true, passive: true });
  window.addEventListener('blur', () => window.setTimeout(sendIfInterstitialFocused, 100));
  window.addEventListener('pagehide', sendIfInterstitialFocused);
  document.addEventListener('visibilitychange', sendIfInterstitialFocused);

  armInterstitialTracking();
};
